import { createSelector } from 'reselect'

import { getPostalCode, getGender, getDateOfBirth } from 'models/ProfileInfo/selectors'
import { getRegion } from 'models/General/selectors'
import { FETCH_STATUSES } from 'resources'

import {
  getProfileRewardsRequiredQuestions,
  groupProfileRewardsQuestions,
  sortGroupedProfileRewardsQuestions,
} from './utils'

// Profile Rewards Questions
export const getIsFetchingQuestions = ({ profileRewards }) => profileRewards?.fetchStatus === FETCH_STATUSES.FETCHING
export const getAttributesCount = ({ profileRewards }) => profileRewards.attributesCount
export const getAttributesWithRequiredQuestionsCount = createSelector(
  [getAttributesCount, getPostalCode, getGender, getDateOfBirth],
  (attributesCount, postalCode, gender, dateOfBirth) =>
    attributesCount + (postalCode ? 1 : 0) + (gender ? 1 : 0) + (dateOfBirth ? 1 : 0),
)
export const getProfileRewardsQuestions = ({ profileRewards }) => profileRewards.profileRewardsQuestions ?? []
export const getGroupedProfileRewardsQuestions = createSelector(
  getProfileRewardsQuestions,
  groupProfileRewardsQuestions,
)
export const getRequiredProfileRewardsQuestions = createSelector(
  [getRegion, getPostalCode, getGender, getDateOfBirth],
  (region, postalCode, gender, dateOfBirth) =>
    getProfileRewardsRequiredQuestions(region, postalCode, gender, dateOfBirth),
)
export const getGroupedProfileRewardsQuestionsWithRequiredQuestions = createSelector(
  [getRequiredProfileRewardsQuestions, getProfileRewardsQuestions],
  (requiredProfileRewardsQuestions, questions) =>
    groupProfileRewardsQuestions([...requiredProfileRewardsQuestions, ...questions]),
)
export const getSortedGroupedProfileRewardsQuestionsWithRequiredQuestions = createSelector(
  getGroupedProfileRewardsQuestionsWithRequiredQuestions,
  sortGroupedProfileRewardsQuestions,
)

// Full Unveil Profile Rewards Questions
export const getHasFullUnveilData = ({ profileRewards }) => !!profileRewards.fullUnveil
export const getFullUnveilAttributesCount = ({ profileRewards }) => profileRewards.fullUnveil?.attributesCount ?? null
export const getFullUnveilProfilesCount = ({ profileRewards }) => profileRewards.fullUnveil?.profilesCount ?? 0
export const getFullUnveilQuestions = ({ profileRewards }) => profileRewards.fullUnveil?.questions ?? null
export const getGroupedFullUnveilProfileRewardsQuestions = createSelector(
  getFullUnveilQuestions,
  groupProfileRewardsQuestions,
)
export const getSortedGroupedFullUnveilProfileRewardsQuestions = createSelector(
  getGroupedFullUnveilProfileRewardsQuestions,
  sortGroupedProfileRewardsQuestions,
)

// Full Unveil Associated Ids
export const getFullUnveilAssociatedIds = ({ profileRewards }) => profileRewards.fullUnveil?.associatedIds ?? []

// TODO: use this state selector for creating separate Unveil Scanning screen
export const getFullUnveilIsAssociatedIdsHandled = ({ profileRewards }) =>
  !!profileRewards.fullUnveil?.isAssociatedIdsHandled

export const getFullUnveilAssociatedIdsCount = createSelector(
  getFullUnveilAssociatedIds,
  (associatedIds) => associatedIds?.length ?? 0,
)

// Full Unveil Conflict Resolution
export const getFullUnveilConflicts = ({ profileRewards }) => profileRewards.fullUnveil?.conflicts ?? []

// Full Unveil Breach Data
export const getFullUnveilBreachData = ({ profileRewards }) => profileRewards.fullUnveil?.breachData ?? null
export const getFullUnveilBreachesCount = createSelector(
  getFullUnveilBreachData,
  (breachData) => breachData?.breachesCount ?? 0,
)
export const getFullUnveilPasswordsCount = createSelector(
  getFullUnveilBreachData,
  (breachData) => breachData?.passwordsCount ?? 0,
)
export const getFullUnveilBreachesAttributesCount = createSelector(
  getFullUnveilBreachData,
  (breachData) => breachData?.breachesAttributesCount ?? 0,
)
export const getFullUnveilIdBreaches = createSelector(
  getFullUnveilBreachData,
  (breachData) => breachData?.idBreaches ?? [],
)

export const getFullUnveilIsBreachDataScanned = createSelector(
  getFullUnveilBreachData,
  (breachData) => !!breachData?.isDataBreachesScanned,
)

export const getIsProspectDataBreachesScanned = createSelector(
  getFullUnveilBreachData,
  (breachData) => !!breachData?.isProspectDataBreachesScanned,
)

export const getIsProspectDataBreachesFound = createSelector(
  getFullUnveilBreachData,
  (breachData) => !!breachData?.isProspectDataBreachesFound,
)

export const getFullUnveilTotalDataBreachSinceLastAcknowledged = createSelector(
  getFullUnveilBreachData,
  (breachData) => breachData?.totalDataBreachSinceLastAcknowledged ?? 0,
)

export const getFullUnveilNewBreachesCount = createSelector(getFullUnveilBreachData, (breachData) =>
  breachData?.idBreaches.reduce(
    (idAcc, { breaches }) => idAcc + breaches.reduce((newAcc, breach) => newAcc + (breach.isNew ? 1 : 0), 0),
    0,
  ),
)

export const getFullUnveilNewBreachIdsCount = createSelector(getFullUnveilBreachData, (breachData) =>
  breachData?.idBreaches.reduce(
    (idAcc, { breaches, lastAcknowledgedDatetime }) =>
      lastAcknowledgedDatetime == null ? idAcc + breaches.length : idAcc,
    0,
  ),
)

export const getMyFootprintBreachData = ({ profileRewards }) => profileRewards.breachData ?? null

export const getMyFootprintBreachDataIdBreaches = createSelector(
  getMyFootprintBreachData,
  (breachData) => breachData?.idBreaches ?? [],
)

export const getMyFootprintBreachDataPasswordsCount = createSelector(
  getMyFootprintBreachData,
  (breachData) => breachData?.passwordsCount ?? 0,
)

export const getMyFootprintIsBreachDataScanned = createSelector(
  getMyFootprintBreachData,
  (breachData) => !!breachData?.isDataBreachesScanned,
)

export default {
  getIsFetchingQuestions,
  getAttributesCount,
  getAttributesWithRequiredQuestionsCount,
  getProfileRewardsQuestions,
  getGroupedProfileRewardsQuestions,
  getSortedGroupedProfileRewardsQuestionsWithRequiredQuestions,
  getRequiredProfileRewardsQuestions,
  getHasFullUnveilData,
  getFullUnveilAttributesCount,
  getFullUnveilProfilesCount,
  getFullUnveilQuestions,
  getGroupedFullUnveilProfileRewardsQuestions,
  getSortedGroupedFullUnveilProfileRewardsQuestions,
  getFullUnveilAssociatedIds,
  getFullUnveilIsAssociatedIdsHandled,
  getFullUnveilAssociatedIdsCount,
  getFullUnveilConflicts,
  getFullUnveilBreachData,
  getFullUnveilBreachesCount,
  getFullUnveilPasswordsCount,
  getFullUnveilBreachesAttributesCount,
  getFullUnveilIdBreaches,
  getFullUnveilIsBreachDataScanned,
  getIsProspectDataBreachesScanned,
  getIsProspectDataBreachesFound,
  getFullUnveilTotalDataBreachSinceLastAcknowledged,
  getFullUnveilNewBreachesCount,
  getFullUnveilNewBreachIdsCount,
  getMyFootprintBreachData,
  getMyFootprintBreachDataIdBreaches,
  getMyFootprintBreachDataPasswordsCount,
  getMyFootprintIsBreachDataScanned,
}
